// Strato dati: usa l'API reale; in dev/offline o senza sessione ripiega sul
// dataset demo (src/lib/demo.ts) così le schermate restano ispezionabili.
import { api } from './api';
import {
  DEMO_DOCUMENTS,
  DEMO_USER,
  DEMO_WARRANTIES,
  demoAlerts,
  demoMembers,
  demoWarrantyResponse,
} from './demo';
import type {
  AlertsResponse,
  DocumentDTO,
  MeDTO,
  MemberCreateInput,
  MemberDTO,
  WarrantyDTO,
  WarrantyListResponse,
} from '@shared/types';

// Modalità demo: attivata da AuthProvider quando il backend non è
// raggiungibile (es. `vite dev` senza wrangler). Quando spenta, gli errori
// API si propagano alle schermate (stati error/empty reali).
let demoMode = false;
export function setDemoMode(on: boolean): void {
  demoMode = on;
}

async function withFallback<T>(call: () => Promise<T>, fallback: () => T): Promise<T> {
  if (demoMode) return fallback();
  try {
    return await call();
  } catch (e) {
    // Senza sessione o backend KO durante la navigazione: ripiega su demo
    // solo se la modalità demo è attiva; altrimenti rilancia.
    if (demoMode) return fallback();
    throw e;
  }
}

export const store = {
  me: (): Promise<MeDTO> => withFallback(() => api.me(), () => DEMO_USER),

  documents: (params?: { type?: string; q?: string; member_id?: string }): Promise<DocumentDTO[]> =>
    withFallback(
      async () => (await api.listDocuments(params)).items,
      () => filterDemoDocs(params),
    ),

  document: (id: string): Promise<DocumentDTO | null> =>
    withFallback(
      () => api.getDocument(id),
      () => DEMO_DOCUMENTS.find((d) => d.id === id) ?? null,
    ),

  warranties: (params?: { status?: string; q?: string; member_id?: string }): Promise<WarrantyListResponse> =>
    withFallback(
      () => api.listWarranties(params),
      () => filterDemoWarranties(params),
    ),

  warranty: (id: string): Promise<WarrantyDTO | null> =>
    withFallback(
      () => api.getWarranty(id),
      () => DEMO_WARRANTIES.find((w) => w.id === id) ?? null,
    ),

  alerts: (): Promise<AlertsResponse> => withFallback(() => api.listAlerts(), () => demoAlerts()),

  members: (): Promise<MemberDTO[]> =>
    withFallback(async () => (await api.listMembers()).items, () => demoMembers().items),

  createMember: (input: MemberCreateInput): Promise<MemberDTO> =>
    withFallback(
      () => api.createMember(input),
      () => ({ id: `m_${Date.now()}`, display_name: input.display_name, relation: input.relation ?? 'other', is_self: false, doc_count: 0 }),
    ),
};

function filterDemoDocs(params?: { type?: string; q?: string; member_id?: string }): DocumentDTO[] {
  let items = DEMO_DOCUMENTS;
  if (params?.member_id) items = items.filter((d) => d.owner_member_id === params.member_id);
  if (params?.type && params.type !== 'tutti') items = items.filter((d) => d.type === params.type);
  if (params?.q) {
    const q = params.q.toLowerCase();
    items = items.filter(
      (d) => d.title.toLowerCase().includes(q) || d.holder.toLowerCase().includes(q) || (d.number ?? '').toLowerCase().includes(q),
    );
  }
  return items;
}

function filterDemoWarranties(params?: { status?: string; q?: string; member_id?: string }): WarrantyListResponse {
  const base = demoWarrantyResponse();
  let items = base.items;
  if (params?.member_id) items = items.filter((w) => w.owner_member_id === params.member_id);
  if (params?.q) {
    const q = params.q.toLowerCase();
    items = items.filter((w) => w.product.toLowerCase().includes(q) || (w.seller ?? '').toLowerCase().includes(q));
  }
  if (params?.status && ['active', 'expiring', 'expired'].includes(params.status)) {
    items = items.filter((w) => w.status === params.status);
  }
  return { summary: base.summary, items };
}
